import { HttpException, HttpStatus, Injectable, Scope } from '@nestjs/common';
import { FindManyOptions, In, ObjectLiteral, Repository } from 'typeorm';
import { GetDataloaderInput, ResolveInput,
  ResolveType, ResultType } from './types';
import DataLoader, { BatchLoadFn } from 'dataloader';
/**
 * Wrapper class for Dataloader.
 */
@Injectable({ scope: Scope.REQUEST })
export class BaseDataLoader<V extends ObjectLiteral, K = string> {
  private dataloader: DataLoader<K, V> | DataLoader<K, V[]>;
  private valueRepo: Repository<V>;
  private type: ResolveType;
  private resolveInput?: ResolveInput<V>;
  private fieldName: string;
  private resultType?: ResultType;

  constructor(inp: GetDataloaderInput<V>) {
    // getRepository has been changed to dataSource.getRepository for TypeORM versions above 3.0
    this.valueRepo = dataSource.getRepository(inp.fromEntity);
    this.type = inp.resolveType || 'one';
    this.resolveInput = inp.resolveInput;
    this.fieldName = inp.fieldName || 'id';
    this.resultType = inp.resultType || 'entity';
    //TODO: use strict types
    this.dataloader =
      this.type === 'one'
        ? new DataLoader<K, V>(this.getBatchLoadFunction() as BatchLoadFn<K, V>)
        : this.resultType === 'count' ? new DataLoader<K, V>(this.getBatchLoadFunction() as BatchLoadFn<K, V>)
        : new DataLoader<K, V[]>(
            this.getBatchLoadFunction() as BatchLoadFn<K, V[]>
          );
    if ((this.type === 'many-to-many') && (this.resultType !== 'count')) {
      if (!this.resolveInput) {
        this.resolveInput = {
          relations: [this.fieldName],
        };
      } 
      else if (typeof this.resolveInput === 'object' &&
        !this.resolveInput.relations?.includes(this.fieldName)
      ) 
      {
        this.resolveInput = {
          ...this.resolveInput,
          relations: [...(this.resolveInput.relations ?? []), this.fieldName],
        };
      }
    }
  }

  public getBatchLoadFunction(): (keys: readonly K[]) => Promise<V[] | V[][]> {
    return async (keys: readonly K[]) => {
      const isCountType = this.resultType === 'count';
      const values = await this.getValuesFromKeys(keys as K[], isCountType);    
      if (isCountType) {
        return keys.map((key) => {
          const value = values.find((value) => value.key === key);
          return value ? { [key as unknown as string]: value.count } : { [key as unknown as string]: 0 }
        }) as V[];
      }
      let result;
      if (this.type === 'one') {
        const map = new Map<K, V>();
        values.forEach(value => {
          //TODO: use strict types
          map.set((value as any)[this.fieldName], value);
        });
        result = keys.map(key => {
          //TODO: is this the optimal way for type safety?
          return map.get(key) || {};
        });
        return result as V[];
      } else {
        const map = new Map<K, V[]>();
        const pushToMap = (key: K, value: V) => {
          const valArr = map.get(key);
          valArr?.push(value);
          map.set(key, valArr as V[]);
        };
        values.forEach(value => {
          let fieldName;
          if (this.type === 'many') {
            fieldName = (value as any)[this.fieldName];
            //TODO: use strict types
            if (!map.has(fieldName)) {
              map.set((value as any)[this.fieldName], [value]);
              return;
            }
            pushToMap(fieldName, value);
          } else if (this.type === 'many-to-many') {
            for (const entry of value[this.fieldName]) {
              fieldName = entry.id;
              if (!map.has(fieldName)) {
                map.set(fieldName, [value]);
              } else {
                pushToMap(fieldName, value);
              }
            }
          }
        });
        result = keys.map(key => {
          return map.get(key) || [];
        });
        return result as V[][];
      }
    };
  }

  private async getValuesFromKeys(keys: K[], isCountType: boolean): Promise<V[]> {
    let findQueryObj: FindManyOptions<V> = {
      where: {
        [this.fieldName]:
          this.type === 'many-to-many' ? { id: In(keys) } : In(keys),
      },
    } as FindManyOptions<V>;
    let values: V[] = [];
    // CASE: When result type is count
    if (typeof this.resolveInput === 'function') {
      //CASE: Custom function received to find values from keys
      values = await this.resolveInput(keys);
    } else {
      if (isCountType) {
        if (!this.resolveInput) {
          values = await this.getFieldCountObject(keys)
        } else {
            throw new HttpException('resolveInput cannot be added when the resultType is set to count', HttpStatus.BAD_REQUEST);
        }
      }
      else {
        if (!this.resolveInput) {
          //CASE: No resolve input. Default find-query will be used.
          values = await this.valueRepo.find(findQueryObj);
        } else {
          //CASE: Find query object received. Using in conjunction with fieldName
          const where = [
            {
              ...this.resolveInput.where,
              ...findQueryObj.where,
            },
          ];
          findQueryObj = {
            ...this.resolveInput,
            where,
          };
          values = await this.valueRepo.find(findQueryObj);
        }
      }
    }
    return values;
  }

  private async getFieldCountObject(keys: K[]) {
    const isMany = this.type === 'many-to-many';
    const entityName = this.valueRepo.metadata.targetName;
    const joinTableName = this.valueRepo.metadata.findRelationWithPropertyPath(this.fieldName)?.inverseEntityMetadata.targetName;
    const tableName = isMany ? joinTableName : entityName;
    const fieldName = isMany ? 'id' : this.fieldName;
    const query = this.valueRepo.createQueryBuilder()
      .select([`${tableName}.${fieldName} as key`])
      .addSelect(`COALESCE(COUNT(*), 0) as count`)
      .where(`${tableName}.${fieldName} IN (:...keys)`, { keys })
      .groupBy(`${tableName}.${fieldName}`)
    if (this.type === 'many-to-many') {
      return await query.leftJoin(`${entityName}.${this.fieldName}`, `${joinTableName}`).getRawMany();
    }
    return await query.getRawMany();
  }

  public getDataLoader() {
    return this.dataloader;
  }
}
